import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Bike, GraduationCap, TrendingUp, Shield, Smartphone, Users, Star, MessageCircle } from 'lucide-react';
import { FadeIn } from '@/components/Animations';
import ContactForm from '@/components/ContactForm';

const BIKE_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/48y6aysc_image.png";
const RIDER_IMAGE = "https://images.unsplash.com/photo-1588620862555-4da1a7c523b8?auto=format&fit=crop&q=80&w=800";

const journeySteps = [
  {
    step: 1,
    title: 'Join the Program',
    description: 'Register through Gig Fleet and complete your initial assessment.',
    icon: Users,
  },
  {
    step: 2,
    title: 'Complete Training',
    description: 'Access safety training, road skills, and business fundamentals.',
    icon: GraduationCap,
  },
  {
    step: 3,
    title: 'Start Earning',
    description: 'Receive job assignments and start building your track record.',
    icon: Bike,
  },
  {
    step: 4,
    title: 'Grow Your Business',
    description: 'Progress through levels and unlock ownership opportunities.',
    icon: TrendingUp,
  },
];

const benefits = [
  {
    icon: Shield,
    title: 'Safety Support',
    description: 'Access to safety training, equipment guidance, and daily check-ins.',
  },
  {
    icon: GraduationCap,
    title: 'Training Programs',
    description: 'Free access to business skills, financial literacy, and personal development courses.',
  },
  {
    icon: Smartphone,
    title: 'Technology Tools',
    description: 'Use the Gig Fleet app for smart job matching, earnings tracking, and growth insights.',
  },
  {
    icon: TrendingUp,
    title: 'Growth Pathway',
    description: 'Clear progression from rider to fleet owner with mentorship at every stage.',
  },
  {
    icon: Users,
    title: 'Community',
    description: 'Join a supportive network of fellow riders and experienced mentors.',
  },
  {
    icon: Star,
    title: 'Recognition',
    description: 'Earn badges, rewards, and visibility for your achievements and growth.',
  },
];

const ForRidersPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="for-riders-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-yellow-50 via-slate-50 to-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-safety-yellow text-slate-900 rounded-full text-sm font-semibold tracking-wide uppercase">
                  For Riders
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900">
                  Your Journey to <span className="text-primary-mlm">Gigpreneur</span>
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  You're not just a delivery rider — you're a business owner in the making. 
                  Join #MyLifeMatters and access the training, tools, and support you need to thrive.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    asChild
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold shadow-lg"
                    data-testid="rider-join-btn"
                  >
                    <a href="#join-form">
                      Join Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 font-semibold"
                  >
                    <a href="https://wa.me/27720286584?text=Hi%2C%20I%27m%20a%20rider%20interested%20in%20joining%20%23MyLifeMatters" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 w-5 h-5" />
                      WhatsApp Us
                    </a>
                  </Button>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <img
                  src={BIKE_IMAGE}
                  alt="MLM Branded Bike"
                  className="rounded-3xl shadow-xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-safety-yellow rounded-2xl p-6 shadow-xl">
                  <Bike className="w-8 h-8 text-slate-900 mb-2" />
                  <p className="text-lg font-semibold text-slate-900">Ride. Grow. Own.</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Your Gigpreneur Journey
              </h2>
              <p className="text-lg text-slate-600">
                From survival to sustainability — we've mapped out your path to success.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-4 gap-8">
            {journeySteps.map((step, index) => (
              <FadeIn key={step.step} delay={0.1 * index}>
                <div className="relative">
                  {index < journeySteps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-slate-200" />
                  )}
                  <div className="bg-slate-50 rounded-2xl p-6 text-center relative z-10">
                    <div className="w-16 h-16 bg-primary-mlm rounded-full flex items-center justify-center mx-auto mb-4">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-primary-mlm text-xs font-bold rounded-full mb-3">
                      Step {step.step}
                    </span>
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                What You Get
              </h2>
              <p className="text-lg text-slate-600">
                Comprehensive support designed to help you succeed.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <FadeIn key={benefit.title} delay={0.1 * index}>
                <div className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-primary-mlm" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary-mlm to-purple-700">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-8 h-8 text-safety-yellow" fill="#FACC15" />
                ))}
              </div>
              <blockquote className="text-2xl md:text-3xl font-heading text-white mb-8 leading-relaxed">
                "Before #MyLifeMatters, I was just surviving. Now I have training, support, and 
                a clear path to owning my own fleet. My life has changed completely."
              </blockquote>
              <p className="text-purple-200">— Thabo M., Johannesburg Rider</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Join Form */}
      <section id="join-form" className="py-20 md:py-32 bg-white scroll-mt-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-safety-yellow text-slate-900 rounded-full text-sm font-semibold tracking-wide uppercase">
                  Join The Movement
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900">
                  Ready to Start Your Journey?
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Fill out the form below and our team will contact you with next steps. 
                  Your Gigpreneur journey starts here.
                </p>
                <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
                  <h3 className="font-semibold text-slate-900">Requirements:</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary-mlm rounded-full" />
                      Valid driver's license (Code A)
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary-mlm rounded-full" />
                      Own motorcycle or access to one
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary-mlm rounded-full" />
                      Smartphone with data
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary-mlm rounded-full" />
                      Commitment to safety and growth
                    </li>
                  </ul>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="bg-slate-50 rounded-2xl p-8">
                <ContactForm formType="rider" showCompany={false} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForRidersPage;
